import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { checkAddPermission, checkEditPermission, checkDeletePermission } from '../../../Utils/helper';
import { getApi, postApi, putApi, deleteApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import { exportOrders, exportShippingMethods } from '../../../Services/importExport';

function Shipping() {
  const [methodType, setMethodType] = useState('');
  const { data } = useSelector((state) => state.roleData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [newMethodName, setNewMethodName] = useState('');
  const [editingMethod, setEditingMethod] = useState(null);
  const [editMethodName, setEditMethodName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [errors, setErrors] = useState({
    newMethod: '',
    editMethod: ''
  });
  const [touched, setTouched] = useState({
    newMethod: false,
    editMethod: false
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState({
    file: '',
    general: ''
  });
  const [isImporting, setIsImporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchShippingData(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const validateInput = (value, fieldName) => {
    if (!value.trim()) {
      return `${fieldName} is required`;
    }
    if (value.length > 50) {
      return `${fieldName} should be less than 50 characters`;
    }
    // Updated to only allow alphabets and spaces
    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return `${fieldName} should only contain letters and spaces`;
    }
    return '';
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const fetchShippingData = async (search = '') => {
    try {
      setIsLoading(true);
      setError(null);
      const resp = await getApi(`/shipping-method?search=${encodeURIComponent(search)}&per_page=50`);
      if (resp?.success === true) {
        setShippingMethods(resp.data.shippingMethods?.data);
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch shipping details');
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (method) => {
    setMethodToDelete(method);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setMethodToDelete(null);
  };

  const confirmDeleteMethod = async () => {
    if (!methodToDelete) return;

    try {
      setIsLoading(true);
      const response = await deleteApi(`/shipping-method/${methodToDelete.id}`);

      if (response?.success) {
        toast.success(response.msg || 'Shipping method deleted successfully');
        const updatedMethods = shippingMethods.filter(method => method.id !== methodToDelete.id);
        setShippingMethods(updatedMethods);
      } else {
        toast.error(response?.msg || 'Failed to delete shipping method');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to delete shipping method';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      closeDeleteModal();
    }
  };

  const handleNewMethodNameChange = (e) => {
    const value = e.target.value;
    setNewMethodName(value);
    if (touched.newMethod) {
      setErrors({ ...errors, newMethod: validateInput(value, 'Shipping method name') });
    }
  };

  const handleEditMethodNameChange = (e) => {
    const value = e.target.value;
    setEditMethodName(value);
    if (touched.editMethod) {
      setErrors({ ...errors, editMethod: validateInput(value, 'Shipping method name') });
    }
  };

  const handleAddMethod = async () => {
    const validationError = validateInput(newMethodName, 'Shipping method name');
    setErrors({ ...errors, newMethod: validationError });
    setTouched({ ...touched, newMethod: true });

    if (validationError) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', newMethodName);
      const response = await postApi('/shipping-method', formData);

      if (response?.success) {
        toast.success(response.msg || 'Shipping method added successfully');
        const newMethod = {
          id: response.data?.id || Date.now().toString(),
          name: newMethodName,
        };

        setShippingMethods([...shippingMethods, newMethod]);
        setNewMethodName('');
        setMethodType('');
        setErrors({ ...errors, newMethod: '' });
        setTouched({ ...touched, newMethod: false });
        fetchShippingData(searchQuery);
      } else {
        toast.error(response?.msg || 'Failed to add shipping method');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to add shipping method';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditMethod = async () => {
    const validationError = validateInput(editMethodName, 'Shipping method name');
    setErrors({ ...errors, editMethod: validationError });
    setTouched({ ...touched, editMethod: true });

    if (validationError) return;

    try {
      setIsLoading(true);
      const payload = {
        name: editMethodName
      };
      const response = await putApi(`/shipping-method/${editingMethod.id}`, payload);

      if (response?.success) {
        toast.success(response.msg || 'Shipping method updated successfully');
        setShippingMethods(shippingMethods.map(method =>
          method.id === editingMethod.id
            ? { ...method, name: editMethodName }
            : method
        ));
        setEditMethodName('');
        setEditingMethod(null);
        setMethodType('');
        setErrors({ ...errors, editMethod: '' });
        setTouched({ ...touched, editMethod: false });
        fetchShippingData(searchQuery);
      } else {
        toast.error(response?.msg || 'Failed to update shipping method');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to update shipping method';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditForm = (method) => {
    setEditingMethod(method);
    setEditMethodName(method.name);
    setMethodType('edit');
    setErrors({ ...errors, editMethod: '' });
    setTouched({ ...touched, editMethod: false });
  };

  const handleImportClick = () => {
    setShowImportModal(true);
    setImportFile(null);
    setImportErrors({ file: '', general: '' });
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportErrors({ file: '', general: '' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/octet-stream'
    ];

    const isValidType = validTypes.includes(file.type) ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    if (!isValidType) {
      setImportErrors({ ...importErrors, file: 'Only Excel files (.xlsx, .xls) are allowed' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setImportErrors({ ...importErrors, file: 'File size should not exceed 2MB' });
      return;
    }
    setImportFile(file);
    setImportErrors({ ...importErrors, file: '' });
  };

  const downloadSampleFile = async () => {
    const response = await exportOrders(
      {
        is_sample: 1,
      },
    );
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      setImportErrors({
        ...importErrors,
        file: 'Please select a file to import',
        general: ''
      });
      return;
    }

    try {
      setIsImporting(true);
      setImportErrors({ file: '', general: '' });

      const formData = new FormData();
      formData.append('file', importFile);

      const response = await postApi('/shipping-method/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response?.success) {
        toast.success(response.msg || 'Shipping methods imported successfully');
        fetchShippingData(searchQuery);
        closeImportModal();
      } else {
        setImportErrors({
          ...importErrors,
          general: response?.msg || 'Failed to import shipping methods'
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to import shipping methods';
      setImportErrors({ ...importErrors, general: errorMsg });
    } finally {
      setIsImporting(false);
    }
  };

  const handleUpdateData = (data, val) => {
    const value = val.target.checked;
    setShippingMethods((prevMethods) =>
      prevMethods.map((method) =>
        method.id === data.id
          ? { ...method, status: value }
          : method
      )
    );
  };

  const handleUpdateAll = (isChecked) => {
    setShippingMethods(prevMethods =>
      prevMethods.map(method => ({
        ...method,
        status: isChecked
      }))
    );
  };

  const handleExport = async () => {
    const idsStatus = shippingMethods
      .filter(item => 'status' in item && item.status === true)
      .map(item => item.id);
    const val = {
      search: searchQuery,
      ids: idsStatus,
    };
    const res = await exportShippingMethods(val, 'Export_Shipping.xlsx');
  };

  return (
    <div className="RightSideDiv product-single category">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                onClick={closeDeleteModal}
                className="close"
                alt="Close"
              />
            </div>
            <div className="modal-body mb-0 p-0">
              <p>Are you sure you want to delete the shipping method: <strong>{methodToDelete?.name}</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="reset" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button
                className="save mt-0"
                onClick={confirmDeleteMethod}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay file-upload-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Import Shipping Methods</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                onClick={closeImportModal}
                className="close"
                alt="Close"
              />
            </div>
            <div className="modal-body mb-0 p-0">
              <div className="row">
                <div className="col-sm-12">
                  <div className="form-group">
                    <div className="file-upload-wrapper">
                      <label>
                        <form action="">
                          <img src={window.getPublicUrl('/assets/images/file-download-import-icon.svg')} />
                          <p>Select File</p>
                          <input
                            type="file"
                            id="importFile"
                            accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={handleFileChange}
                            className={`file-upload-input ${importErrors.file ? 'is-invalid' : ''}`}
                          />
                        </form>
                      </label>
                      {importErrors.file && (
                        <div className="invalid-feedback d-block" style={{ color: 'red', marginTop: '5px' }}>
                          <i className="fas fa-exclamation-circle me-2"></i>
                          {importErrors.file}
                        </div>
                      )}
                      {importFile && (
                        <div className="file-info mt-4">
                          <p className='selected-name mb-0'>
                            <strong className='me-1'>Selected File: </strong>{importFile.name} ({Math.round(importFile.size / 1024)} KB)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <div className="download-sample">
                    <div className="d-flex justify-content-between py-3">
                      <p>Supported format:.xlsx </p>
                      <p> (Max size: 2MB)</p>
                    </div>
                    <button
                      className="btn btn-link p-0 text-primary"
                      onClick={downloadSampleFile}
                    >
                      Download Sample File
                    </button>
                  </div>
                </div>
              </div>
              {importErrors.general && (
                <div className="alert alert-danger mt-3" style={{ marginBottom: '0' }}>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {importErrors.general}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="reset" onClick={closeImportModal}>
                Cancel
              </button>
              <button
                className="save mt-0"
                onClick={handleImportSubmit}
                disabled={isImporting}
              >
                {isImporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Importing...
                  </>
                ) : (
                  'Import'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Shipping Method Form */}
      {methodType === 'add' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Add Shipping Method</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  setNewMethodName('');
                  setErrors({ ...errors, newMethod: '' });
                  setTouched({ ...touched, newMethod: false });
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Shipping Method Name<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.newMethod && errors.newMethod ? 'is-invalid' : ''}`}
                        placeholder="Enter Shipping Method Name"
                        value={newMethodName}
                        onChange={handleNewMethodNameChange}
                        onBlur={() => {
                          setTouched({ ...touched, newMethod: true });
                          setErrors({
                            ...errors,
                            newMethod: validateInput(newMethodName, 'Shipping method name')
                          });
                        }}
                        maxLength={50}
                        pattern="[a-zA-Z\s]+"
                        title="Shipping method name should only contain letters and spaces"
                      />
                      {touched.newMethod && errors.newMethod && (
                        <div className="invalid-feedback d-block">
                          {errors.newMethod}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setNewMethodName('');
                        setErrors({ ...errors, newMethod: '' });
                        setTouched({ ...touched, newMethod: false });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleAddMethod}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Shipping Method Form */}
      {methodType === 'edit' && editingMethod && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Edit Shipping Method</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  setEditMethodName('');
                  setEditingMethod(null);
                  setErrors({ ...errors, editMethod: '' });
                  setTouched({ ...touched, editMethod: false });
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Shipping Method Name<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.editMethod && errors.editMethod ? 'is-invalid' : ''}`}
                        placeholder="Enter Shipping Method Name"
                        value={editMethodName}
                        onChange={handleEditMethodNameChange}
                        onBlur={() => {
                          setTouched({ ...touched, editMethod: true });
                          setErrors({
                            ...errors,
                            editMethod: validateInput(editMethodName, 'Shipping method name')
                          });
                        }}
                        maxLength={50}
                        pattern="[a-zA-Z\s]+"
                        title="Shipping method name should only contain letters and spaces"
                      />
                      {touched.editMethod && errors.editMethod && (
                        <div className="invalid-feedback d-block">
                          {errors.editMethod}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setEditMethodName(editingMethod.name);
                        setErrors({ ...errors, editMethod: '' });
                        setTouched({ ...touched, editMethod: false });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleEditMethod}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Updating...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Methods List */}
      {methodType === '' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right setting-right-table">
              <div className="table-header">
                <div className="search-card">
                  <img src={window.getPublicUrl('/assets/images/search-icon.svg')} alt="Search" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Shipping Method..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="table-actions">
                {checkAddPermission(data?.permissions, 'shipping_method') && (
                  <button className="btn" onClick={handleImportClick}>
                    <img src={window.getPublicUrl('/assets/images/import.png')} alt="Import" />
                    Import
                  </button>
                )}
                  <button className="btn" onClick={() => handleExport()}>
                    <img src={window.getPublicUrl('/assets/images/export.svg')} alt="Export" />
                    Export
                  </button>
                  {checkAddPermission(data?.permissions, 'shipping_method') && (
                    <button className="btn add" onClick={() => setMethodType('add')}>
                      + Add New Method
                    </button>
                  )}
                </div>
              </div>

              <div className="permission-table-inner">
                <table className="custom-table table-striped">
                  <thead>
                    <tr>
                      <th className="checkbox">
                        <label className="checbox-cs">
                          <input type="checkbox" onClick={(e) => handleUpdateAll(e?.target?.checked)} />
                          <span className="checkmark"></span>
                        </label>
                      </th>
                      <th className="code">Sr. No.</th>
                      <th className="name">Name</th>
                      <th className="action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="4" className="text-center text-danger">{error}</td>
                      </tr>
                    ) : shippingMethods?.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">No shipping methods found</td>
                      </tr>
                    ) : (
                      shippingMethods?.map((method, index) => (
                        <tr key={method.id}>
                          <td className="checkbox">
                            <label className="checbox-cs">
                              <input type="checkbox" onClick={(e) => handleUpdateData(method, e)} checked={method?.status} />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="code">{index + 1}</td>
                          <td className="name">{method.name}</td>
                          <td className="action action-cs">
                            <div className="dropdown">
                               {(checkEditPermission(data?.permissions, 'shipping_method') || checkDeletePermission(data?.permissions, 'shipping_method')) && (
                              <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                Action <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" className="ps-2" />
                              </button>
                               )}
                              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li>
                                {checkEditPermission(data?.permissions, 'shipping_method') && (
                                    <a href="#" className='dropdown-item' onClick={() => openEditForm(method)}>Edit</a>
                                )}
                                </li>
                                <li>
                              {checkDeletePermission(data?.permissions, 'shipping_method') && (
                                    <a className='dropdown-item' href="#" onClick={() => openDeleteModal(method)}> Delete</a>
                                )}
                              </li>
                              </ul>
                              
                             
                            
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Shipping;